import React from "react";

import { FormCheckboxGroup, FormField, FormRadioButtons, PanelFilters } from "@performyard/py-components";

import { SingleSelectEmployee } from "../../../components/Inputs/SelectEmployee";
import type { EmployeeValue } from "../../../components/Inputs/SelectEmployee";
import GroupSelect, { type Group } from "../../../components/Inputs/SelectGroup";

import { DATE_RANGE_OPTIONS } from "../config";
import {
	type CompanyMeetingsCoverageFilterForm,
	CompanyMeetingsCoverageFilterFormSchema,
	type CompanyMeetingsCoverageFilters,
	type DateRange,
	WITHOUT_MEETINGS_OPTION_VALUE,
} from "../schema";

type CompanyMeetingsCoverageFilterPanelProps = {
	isOpen: boolean;
	onClose: () => void;
	filters: CompanyMeetingsCoverageFilters;
	onApplyFilters: (filters: CompanyMeetingsCoverageFilters) => void;
};

const dateRangeOptions = DATE_RANGE_OPTIONS.map((option) => ({ value: option.value, label: option.label }));

const withoutMeetingsOptions = [
	{ value: WITHOUT_MEETINGS_OPTION_VALUE, label: "Only Show Employees without Meetings" },
];

export const CompanyMeetingsCoverageFilterPanel = ({
	isOpen,
	onClose,
	filters,
	onApplyFilters,
}: CompanyMeetingsCoverageFilterPanelProps) => {
	const defaultValues: CompanyMeetingsCoverageFilterForm = {
		employee: filters.employee ?? null,
		manager: filters.manager ?? null,
		group: filters.group ?? null,
		dateRange: filters.dateRange ?? "all_time",
		withoutMeetings: filters.withoutMeetings ? [WITHOUT_MEETINGS_OPTION_VALUE] : [],
	};

	const handleSubmit = ({ value }: { value: CompanyMeetingsCoverageFilterForm }) => {
		const appliedFilters: Partial<CompanyMeetingsCoverageFilters> = {};

		if (value.employee) {
			appliedFilters.employee = value.employee;
		}

		if (value.manager) {
			appliedFilters.manager = value.manager;
		}

		if (value.group) {
			appliedFilters.group = value.group;
		}

		if (value.dateRange && value.dateRange !== "all_time") {
			appliedFilters.dateRange = value.dateRange;
		}

		if (value.withoutMeetings?.includes(WITHOUT_MEETINGS_OPTION_VALUE)) {
			appliedFilters.withoutMeetings = true;
		}

		onApplyFilters(appliedFilters);
		onClose();
	};

	return (
		<PanelFilters
			isOpen={isOpen}
			onClose={onClose}
			schema={CompanyMeetingsCoverageFilterFormSchema}
			defaultValues={defaultValues}
			onSubmit={handleSubmit}
		>
			<div className="space-y-4">
				<FormField<EmployeeValue | null>
					id="employee"
					label="Employee"
					input={(field) => (
						<SingleSelectEmployee
							value={field.value ?? undefined}
							onChange={(id, name) => {
								if (id && name) {
									field.onChange({ id, name });
								} else {
									field.onChange(null);
								}
							}}
							placeholder="Make a selection"
						/>
					)}
				/>

				<FormField<EmployeeValue | null>
					id="manager"
					label="Manager"
					input={(field) => (
						<SingleSelectEmployee
							value={field.value ?? undefined}
							onChange={(id, name) => {
								if (id && name) {
									field.onChange({ id, name });
								} else {
									field.onChange(null);
								}
							}}
							placeholder="Make a selection"
						/>
					)}
				/>

				<FormField<Group | null>
					id="group"
					label="Group"
					input={(field) => (
						<GroupSelect
							value={field.value ?? undefined}
							onChange={(group) => field.onChange(group ? { id: group.id, name: group.name } : null)}
							placeholder="Make a selection"
							isClearable
						/>
					)}
				/>

				<FormRadioButtons<DateRange> id="dateRange" label="Date Range" options={dateRangeOptions} />

				<FormCheckboxGroup id="withoutMeetings" options={withoutMeetingsOptions} />
			</div>
		</PanelFilters>
	);
};

export default CompanyMeetingsCoverageFilterPanel;
